import { useEffect } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { futuresBookTradable, futuresStatusMatches, type StandaloneSelection } from './futuresDetail';
import type { GolfMatchIdentity } from '@/lib/golfMatch';
import type { DateFilter, StatusFilter } from './types';

interface Props {
  dateFilter: DateFilter;
  selectedDateIso: string;
  statusFilter: StatusFilter;
  connected: boolean;
  onSelect: (selection: StandaloneSelection) => void;
}

export function GolfMatchBoards({ dateFilter, selectedDateIso, statusFilter, connected, onSelect }: Props) {
  useMarketStore((s) => s.version);
  const setOrderbookNeeds = useMarketStore((s) => s.setOrderbookNeeds);
  const store = useMarketStore.getState();
  const matches = store.getGolfMatches();
  const dated = matches.filter((m) => dateFilter === 'all' || m.date === selectedDateIso);
  const tickersKey = JSON.stringify(dated.map((m) => m.ticker));
  useEffect(() => {
    setOrderbookNeeds('golf-match-board', JSON.parse(tickersKey) as string[]);
    return () => setOrderbookNeeds('golf-match-board', []);
  }, [setOrderbookNeeds, tickersKey]);
  const groups = new Map<string, GolfMatchIdentity[]>();
  for (const match of dated) {
    if (!futuresStatusMatches(store.getMarket(match.ticker)?.status, statusFilter)) continue;
    const group = groups.get(match.key) ?? [];
    group.push(match);
    groups.set(match.key, group);
  }
  if (groups.size === 0) return <p role="status" className="text-muted-foreground py-12 text-center text-sm">
    {connected ? 'No dated Golf matches available for these filters. Outright markets remain under Futures.' : 'Connecting to Hydra...'}
  </p>;
  return <div className="space-y-4">
    {[...groups].map(([key, rows]) => {
      const first = rows[0];
      // Conflicting source headers are visible, but never seat a ticket under
      // whichever sibling happened to arrive first.
      const coherent = rows.every((m) => m.title === first.title && m.startTimeMs === first.startTimeMs);
      return <section key={key} data-view-anchor={`golf:${key}`} className="bg-card border-border min-w-0 rounded-lg border">
        <header className="border-border flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b px-4 py-3">
          <h2 className="min-w-0 break-words text-base font-semibold [overflow-wrap:anywhere]">{coherent ? first.title : 'Golf match identity needs reconciliation'}</h2>
          <span className="text-muted-foreground text-xs">{first.venue === 'kalshi' ? 'Kalshi' : 'Polymarket'} · {new Date(first.startTimeMs).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ET</span>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead><tr className="text-muted-foreground border-border border-b text-left"><th className="px-4 py-2 font-medium">Proposition</th><th className="w-44 px-4 py-2 font-medium">YES reference</th></tr></thead>
            <tbody>{rows.map((row) => {
              const book = row.venue === 'polymarket_us' ? null : store.getGolfMatchBook(row.venue, row.ticker);
              const tradable = coherent && futuresBookTradable(book, row.venue);
              return <tr key={row.ticker} className="border-border/50 border-b last:border-b-0">
                <td className="break-words px-4 py-3 [overflow-wrap:anywhere]">
                  <span>{row.proposition}</span>
                  <div className="text-muted-foreground mt-1 break-all font-mono text-[10px]">{row.ticker}</div>
                </td>
                <td className="px-4 py-3">
                  {tradable ? <button type="button" className="hover:bg-accent rounded border px-3 py-2 text-left" onClick={() => onSelect({
                    kind: 'golf-match', sport: 'golf', boardKey: row.key, venue: row.venue, ticker: row.ticker,
                  })}>{Number(book!.priceCents!.toFixed(4))}¢ · Open ticket</button>
                    : <span className="text-muted-foreground">Unavailable</span>}
                </td>
              </tr>;
            })}</tbody>
          </table>
        </div>
        <p className="text-muted-foreground border-border border-t px-4 py-2 text-xs">Manual limit orders only. YES/NO applies to the named proposition, not the opposing player or pair. Review the venue’s tie and withdrawal rules.</p>
      </section>;
    })}
  </div>;
}
